<template>
	<div class="container py-4">
		<div class="row">
			<div class="col-12">
				<div class="d-flex justify-content-between align-items-center mb-4">
					<div>
						<h1 class="mb-0">
							<i class="bi bi-geo-alt-fill text-primary me-2"></i> District
							Settings
						</h1>
						<p class="text-muted mb-0 small">Changes save automatically.</p>
					</div>
					<SaveIndicator
						:state="saveState"
						:pending="savePending"
						@retry="retrySave"
					/>
				</div>

				<MapServiceArea
					v-if="orgStore.settings?.serviceArea?.geometry"
					:serviceArea="orgStore.settings.serviceArea.geometry"
					:satelliteSurveillanceArea="
						orgStore.settings.satelliteSurveillanceArea?.geometry ?? undefined
					"
					:xmin="orgStore.settings.serviceArea.min.longitude ?? 0"
					:ymin="orgStore.settings.serviceArea.min.latitude ?? 0"
					:xmax="orgStore.settings.serviceArea.max.longitude ?? 0"
					:ymax="orgStore.settings.serviceArea.max.latitude ?? 0"
				/>
				<div class="alert alert-info" v-else>No service area defined</div>

				<div class="row">
					<!-- Basic Information -->
					<div class="col-md-6">
						<div class="card settings-card">
							<div class="card-header bg-light">
								<h5>
									<i class="bi bi-building me-2"></i> Organization Information
								</h5>
							</div>
							<div class="card-body">
								<div class="mb-3">
									<label for="agencyName" class="form-label">
										<i class="bi bi-briefcase me-1"></i> Agency Name
									</label>
									<input
										type="text"
										class="form-control"
										id="name"
										v-model="form.name"
										@change="saveText('name', $event)"
									/>
								</div>
								<div class="mb-3">
									<label for="website" class="form-label">
										<i class="bi bi-globe me-1"></i> Website
									</label>
									<input
										type="url"
										class="form-control"
										id="website"
										v-model="form.website"
										@change="saveText('website', $event)"
									/>
								</div>
								<div class="mb-3">
									<label for="generalManager" class="form-label">
										<i class="bi bi-person-badge me-1"></i> General Manager Name
									</label>
									<input
										type="text"
										class="form-control"
										id="generalManager"
										v-model="form.generalManagerName"
										@change="saveText('generalManagerName', $event)"
									/>
								</div>
							</div>
						</div>
					</div>

					<!-- Contact Information -->
					<div class="col-md-6">
						<div class="card settings-card">
							<div class="card-header bg-light">
								<h5>
									<i class="bi bi-telephone me-2"></i> Contact Information
								</h5>
							</div>
							<div class="card-body">
								<div class="mb-3">
									<label for="address" class="form-label">
										<i class="bi bi-geo me-1"></i> Street
									</label>
									<input
										type="text"
										class="form-control"
										id="address"
										v-model="form.officeAddressStreet"
										@change="saveText('officeAddressStreet', $event)"
									/>
								</div>
								<div class="row">
									<div class="col-md-6 mb-3">
										<label for="city" class="form-label">
											<i class="bi bi-building me-1"></i> City
										</label>
										<input
											type="text"
											class="form-control"
											id="city"
											v-model="form.officeAddressCity"
											@change="saveText('officeAddressCity', $event)"
										/>
									</div>
									<div class="col-md-6 mb-3">
										<label for="postalCode" class="form-label">
											<i class="bi bi-mailbox me-1"></i> Postal Code
										</label>
										<input
											type="text"
											class="form-control"
											id="postalCode"
											v-model="form.officeAddressPostalCode"
											@change="saveText('officeAddressPostalCode', $event)"
										/>
									</div>
								</div>
								<div class="mb-3">
									<label for="phoneNumber" class="form-label">
										<i class="bi bi-telephone me-1"></i> Phone Number
									</label>
									<input
										type="tel"
										class="form-control"
										id="phoneNumber"
										v-model="form.officePhone"
										@change="saveText('officePhone', $event)"
									/>
								</div>
								<div class="mb-3">
									<label for="officeEmail" class="form-label">
										<i class="bi bi-envelope me-1"></i> Office Email
									</label>
									<input
										type="email"
										class="form-control"
										id="officeEmail"
										v-model="form.officeEmail"
										@change="saveText('officeEmail', $event)"
									/>
									<div class="form-text">
										Shown to the public alongside the office phone on the page a
										printed trap code opens.
									</div>
								</div>
								<div class="mb-3">
									<label for="faxNumber" class="form-label">
										<i class="bi bi-printer me-1"></i> Fax Number
									</label>
									<input
										type="tel"
										class="form-control"
										id="faxNumber"
										v-model="form.officeFax"
										@change="saveText('officeFax', $event)"
									/>
								</div>
							</div>
						</div>
					</div>

					<!-- Display Units -->
					<div class="col-12">
						<div class="card settings-card">
							<div class="card-header bg-light">
								<h5>
									<i class="bi bi-rulers me-2"></i> Label Rate Display Units
								</h5>
							</div>
							<div class="card-body">
								<p class="text-muted">
									Pesticide label rates are stored exactly as the label states
									them. Choose the units this district reads them in; the
									product pages show each rate in these units alongside the
									label's own wording.
								</p>
								<div class="row">
									<div class="col-md-4 mb-3">
										<label for="unitPreset" class="form-label">
											<i class="bi bi-sliders me-1"></i> Preset
										</label>
										<select
											class="form-select"
											id="unitPreset"
											v-model="form.unitPreset"
											@change="savePreset($event)"
										>
											<option value="imperial">Imperial</option>
											<option value="metric">Metric</option>
										</select>
										<div class="form-text">
											Supplies every dimension's default.
										</div>
									</div>
									<div class="col-md-4 mb-3">
										<label for="unitArea" class="form-label">Area</label>
										<select
											class="form-select"
											id="unitArea"
											v-model="form.unitArea"
											@change="saveUnit('area', $event)"
										>
											<option value="">Use preset default</option>
											<option value="acre">acre</option>
											<option value="hectare">hectare</option>
											<option value="sq ft">sq ft</option>
											<option value="sq m">m²</option>
										</select>
									</div>
									<div class="col-md-4 mb-3">
										<label for="unitMass" class="form-label">Mass</label>
										<select
											class="form-select"
											id="unitMass"
											v-model="form.unitMass"
											@change="saveUnit('mass', $event)"
										>
											<option value="">Use preset default</option>
											<option value="lb">lb</option>
											<option value="oz">oz</option>
											<option value="g">g</option>
											<option value="kg">kg</option>
											<option value="mg">mg</option>
										</select>
									</div>
									<div class="col-md-4 mb-3">
										<label for="unitVolume" class="form-label">
											Liquid Volume
										</label>
										<select
											class="form-select"
											id="unitVolume"
											v-model="form.unitVolume"
											@change="saveUnit('volume', $event)"
										>
											<option value="">Use preset default</option>
											<option value="gal">gal</option>
											<option value="qt">qt</option>
											<option value="pt">pt</option>
											<option value="fl oz">fl oz</option>
											<option value="L">L</option>
											<option value="mL">mL</option>
										</select>
									</div>
									<div class="col-md-4 mb-3">
										<label for="unitAreaBasis" class="form-label">
											Area Rate Basis
										</label>
										<select
											class="form-select"
											id="unitAreaBasis"
											v-model="form.unitAreaBasis"
											@change="saveUnit('areaBasis', $event)"
										>
											<option value="">Use preset default</option>
											<option value="per acre">per acre</option>
											<option value="per hectare">per hectare</option>
											<option value="per 1000 sq ft">per 1000 sq ft</option>
											<option value="per sq m">per m²</option>
										</select>
									</div>
									<div class="col-md-4 mb-3">
										<label for="unitVolumeBasis" class="form-label">
											Volume Rate Basis
										</label>
										<select
											class="form-select"
											id="unitVolumeBasis"
											v-model="form.unitVolumeBasis"
											@change="saveUnit('volumeBasis', $event)"
										>
											<option value="">Use preset default</option>
											<option value="per gallon">per gallon</option>
											<option value="per L">per L</option>
											<option value="per cu ft">per cu ft</option>
											<option value="per cu m">per m³</option>
										</select>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Organization Coverage Information -->
					<div class="col-12">
						<div class="card settings-card">
							<div class="card-header bg-light">
								<h5><i class="bi bi-map me-2"></i> Service Area Coverage</h5>
							</div>
							<div class="card-body">
								<div class="row">
									<div class="col-md-6 mb-3">
										<label for="totalArea" class="form-label">
											<i class="bi bi-rulers me-1"></i> Total Area (square
											meters)
										</label>
										<input
											type="number"
											class="form-control"
											id="totalArea"
											:value="orgStore.serviceAreaSquareMeters()"
											disabled
										/>
									</div>
								</div>
								<div class="alert alert-info mb-0">
									<i class="bi bi-info-circle me-1"></i>
									The service area and satellite surveillance area are defined
									as part of your service contract. To request changes, please
									contact Nidus Support.
								</div>
							</div>
						</div>
					</div>

					<!-- Routing. The Operations Command Center's drive plan reads
					     these three settings: where the trucks start and finish,
					     the Valhalla costing profile the district drives, and the
					     minutes a stop of each kind is planned with. A plan is a
					     derived read of a Route, so nothing here changes what a
					     Route is, who is on it, or when its stops are due. -->
					<div class="col-12">
						<div class="card settings-card">
							<div class="card-header bg-light">
								<h5>
									<i class="bi bi-signpost-split me-2"></i> Routing
								</h5>
							</div>
							<div class="card-body">
								<p class="text-muted">
									Each Route's drive plan in the Operations Command Center
									starts and ends at the base point below, follows roads for
									the district's travel profile, and allows the per-kind
									service minutes at every stop. A plan never gates dispatch
									and never moves an Assignment.
								</p>

								<div class="row">
									<div class="col-lg-7 mb-3">
										<div class="form-label">
											<i class="bi bi-pin-map me-1"></i> Base point
										</div>
										<div class="routing-map">
											<mgl-map
												v-if="orgStore.settings"
												:map-style="mapStyleURL(ALIDADE_SMOOTH)"
												:center="routingMapCenter"
												:zoom="routingMapZoom"
												@map:load="onRoutingMapLoad"
											>
												<!-- The district the base belongs to, so the
												     operator places the yard by its surroundings. -->
												<mgl-geo-json-source
													source-id="routing-service-area"
													:data="routingServiceArea"
												>
													<mgl-line-layer
														layer-id="routing-service-area-outline"
														:paint="{
															'line-color': '#0d6efd',
															'line-width': 2,
															'line-opacity': 0.6,
														}"
													/>
												</mgl-geo-json-source>
												<mgl-marker
													v-if="form.routingBase"
													:coordinates="routingBaseCoordinates"
													anchor="bottom"
												>
													<template #marker>
														<span class="routing-base-pin" title="Base point">
															<i class="bi bi-truck"></i>
														</span>
													</template>
												</mgl-marker>
											</mgl-map>
											<div
												v-else
												class="d-flex h-100 align-items-center justify-content-center text-muted small"
											>
												Loading map…
											</div>
										</div>
										<div class="form-text">
											Click the map to place the base. A district with no base
											plans stop to stop, and the plan says the morning leg is
											not counted.
										</div>
									</div>

									<div class="col-lg-5 mb-3">
										<div class="mb-3">
											<label class="form-label" for="routingBaseLabel">
												Base label
											</label>
											<input
												type="text"
												class="form-control"
												id="routingBaseLabel"
												placeholder="e.g. Yard"
												v-model="form.routingBaseLabel"
												:disabled="!form.routingBase"
												@change="saveRoutingBaseLabel"
											/>
											<div class="form-text">
												The operator's own name for the base, shown on the
												plan's base row. Place a point first.
											</div>
										</div>
										<button
											type="button"
											class="btn btn-outline-secondary btn-sm"
											:disabled="!form.routingBase"
											@click="clearRoutingBase"
										>
											<i class="bi bi-x-circle me-1"></i> Clear base
										</button>

										<div class="mt-3">
											<label class="form-label" for="routingCosting">
												Costing
											</label>
											<select
												class="form-select"
												id="routingCosting"
												v-model="form.routingCosting"
												@change="saveRoutingCosting"
											>
												<option value="auto">Auto</option>
												<option value="truck">Truck</option>
												<option value="pedestrian">Pedestrian</option>
											</select>
											<div class="form-text">
												Valhalla's travel profile: Auto for a car or light
												pickup, Truck where height and weight limits apply,
												Pedestrian for walking routes. Changing it invalidates
												every stored plan, so the next read recomputes.
											</div>
										</div>
									</div>
								</div>

								<hr />

								<div class="row">
									<div class="col-lg-7">
										<div class="form-label">
											Service minutes by kind
										</div>
										<table class="table table-sm align-middle mb-0">
											<tbody>
												<tr v-for="kind in KIND_OPTIONS" :key="kind.value">
													<td class="w-50">{{ kind.label }}</td>
													<td>
														<input
															type="number"
															class="form-control form-control-sm"
															min="1"
															max="720"
															step="1"
															placeholder="no estimate"
															:aria-label="`Service minutes for ${kind.label}`"
															v-model="form.routingServiceMinutes[kind.value]"
															@change="saveRoutingServiceMinutes"
														/>
													</td>
												</tr>
											</tbody>
										</table>
									</div>
									<div class="col-lg-5">
										<p class="text-muted small mt-3 mt-lg-0">
											The minutes a stop of each kind is planned to take. A
											blank row means the stop is planned with no time
											estimate, and the plan's total says so. A service time
											on the Assignment itself overrides the kind default for
											that one stop.
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Site Fabric -->
					<div class="col-12">
						<div class="card settings-card">
							<div
								class="card-header bg-light d-flex justify-content-between align-items-center flex-wrap gap-2"
							>
								<h5 class="mb-0">
									<i class="bi bi-diagram-3 me-2"></i> Site Fabric
								</h5>
							</div>
							<div class="card-body">
								<p class="text-muted">
									The site fabric is one Site per service-area parcel, plus a
									Site for every street segment in the gaps between them, owned
									by the city or county that holds the right-of-way. It is built
									and rebuilt by Nidus operators from the sudo tools (TIGER);
									this page shows this district's current fabric state.
								</p>

								<div class="d-flex align-items-center flex-wrap mb-3">
									<span class="me-2">Status:</span>
									<span class="badge" :class="fabricStatusClass">
										<span
											v-if="fabricRunning"
											class="spinner-border spinner-border-sm me-1"
											role="status"
											aria-hidden="true"
										></span>
										{{ fabricStatusLabel }}
									</span>
									<span v-if="fabricStageLabel" class="text-muted ms-3">
										Last action: {{ fabricStageLabel }}
									</span>
									<span
										v-if="orgStore.siteFabric?.completedAt"
										class="text-muted ms-3"
									>
										Last completed
										{{ formatTime(orgStore.siteFabric.completedAt) }}
									</span>
									<span
										v-if="orgStore.siteFabric?.startedAt && fabricRunning"
										class="text-muted ms-3"
									>
										Started {{ formatTime(orgStore.siteFabric.startedAt) }}
									</span>
								</div>

								<div
									v-if="orgStore.siteFabric?.error"
									class="alert alert-danger"
								>
									<i class="bi bi-exclamation-triangle me-1"></i>
									{{ orgStore.siteFabric.error }}
								</div>

								<div class="mb-3">
									<div class="text-muted mb-1">
										Counties in this district's service area
									</div>
									<table
										v-if="fabricCounties.length"
										class="table table-sm align-middle mb-0"
									>
										<thead>
											<tr>
												<th>County</th>
												<th>FIPS</th>
												<th>Centrelines</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="c in fabricCounties" :key="c.fips">
												<td>{{ c.name }}</td>
												<td>{{ c.fips }}</td>
												<td>
													<span
														class="badge"
														:class="
															c.ingested
																? 'text-bg-success'
																: 'text-bg-secondary'
														"
													>
														{{ c.ingested ? "Ingested" : "Not ingested" }}
													</span>
													<span v-if="c.ingested" class="text-muted ms-2">
														{{ c.segments.toLocaleString() }} segments
													</span>
												</td>
											</tr>
										</tbody>
									</table>
									<div v-else class="text-muted fst-italic">
										No parcel sources intersect this district's service area
										yet.
									</div>
								</div>

								<div v-if="fabricReport.length" class="table-responsive">
									<div class="text-muted mb-1">
										Verification report (last build)
									</div>
									<table class="table table-sm table-striped align-middle mb-0">
										<thead>
											<tr>
												<th>Metric</th>
												<th>Value</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="m in fabricReport" :key="m.metric">
												<td>{{ m.metric }}</td>
												<td>{{ m.value }}</td>
											</tr>
										</tbody>
									</table>
								</div>
								<div
									v-else-if="orgStore.siteFabric?.status === 'succeeded'"
									class="text-muted fst-italic"
								>
									No verification report from the last action (a build produces
									one).
								</div>
							</div>
						</div>
					</div>

					<!-- Site Tags. Admin-only, like the Symbology page it draws with:
					     the Organization page itself is readable by every member, so
					     the section carries the role check the retired /tags route
					     used to own. The server refuses the writes regardless. -->
					<div v-if="session.isOrgAdmin" class="col-12">
						<div class="card settings-card">
							<div class="card-header bg-light">
								<h5><i class="bi bi-tags me-2"></i> Site Tags</h5>
							</div>
							<div class="card-body">
								<DistrictTags />
							</div>
						</div>
					</div>

					<!-- Site Symbology. Admin-only for the same reason as Site Tags:
					     the Organization page is member-readable, and these are the
					     district's shared symbols and kind defaults. -->
					<div v-if="session.isOrgAdmin" class="col-12">
						<div class="card settings-card">
							<div class="card-header bg-light">
								<h5><i class="bi bi-palette me-2"></i> Site Symbology</h5>
							</div>
							<div class="card-body">
								<DistrictSymbology />
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { useStoreOrganization } from "@/store/organization";
import { useSessionStore } from "@/store/session";
import {
	MglGeoJsonSource,
	MglLineLayer,
	MglMap,
	MglMarker,
	useMap,
	type MglEvent,
} from "vue-maplibre-gl";
import type { MapMouseEvent } from "maplibre-gl";
import type { GeoJSON } from "geojson";
import "maplibre-gl/dist/maplibre-gl.css";
import SaveIndicator from "@/components/common/SaveIndicator.vue";
import DistrictTags from "@/components/configuration/DistrictTags.vue";
import DistrictSymbology from "@/components/configuration/DistrictSymbology.vue";
import { KIND_OPTIONS } from "@/components/operations/wire";
import { useSaveState } from "@/lib/autoSave";
import { ALIDADE_SMOOTH } from "@/map/constants";
import { mapStyleURL } from "@/map/util";
import type { RoutingBase } from "@/type/api";
import {
	saveTextField,
	saveUnitOverride,
	saveUnitPreset,
	type OrganizationTextField,
	type UnitOverrideField,
} from "@/view/configuration/organizationSave";
import MapServiceArea from "../../components/MapServiceArea.vue";

const orgStore = useStoreOrganization();
const session = useSessionStore();

// Every editable field on this page queues its own save here; the indicator
// reports the outcome of the last one. There is no Save button: a change is the
// commit, and a refusal is shown rather than silently dropped.
const {
	state: saveState,
	pending: savePending,
	run: runSave,
	retry: retrySave,
} = useSaveState("organization-settings");

// The last run's report and counties, empty until the fabric has been built.
const fabricReport = computed(() => orgStore.siteFabric?.report ?? []);
const fabricCounties = computed(() => orgStore.siteFabric?.counties ?? []);

// fabricRunning is true while a run is queued or in progress, so the page
// keeps polling for a status change.
const fabricRunning = computed(
	() =>
		orgStore.siteFabric?.status === "queued" ||
		orgStore.siteFabric?.status === "running",
);

const fabricStatusLabel = computed(() => {
	switch (orgStore.siteFabric?.status) {
		case "queued":
			return "Queued";
		case "running":
			return "Running";
		case "succeeded":
			return "Succeeded";
		case "failed":
			return "Failed";
		default:
			return "Never run";
	}
});

const fabricStatusClass = computed(() => {
	switch (orgStore.siteFabric?.status) {
		case "queued":
			return "text-bg-secondary";
		case "running":
			return "text-bg-info";
		case "succeeded":
			return "text-bg-success";
		case "failed":
			return "text-bg-danger";
		default:
			return "text-bg-light border";
	}
});

const fabricStageLabel = computed(() => {
	switch (orgStore.siteFabric?.stage) {
		case "counties":
			return "ingest county centrelines";
		case "build":
			return "build / rebuild fabric";
		case "run":
			return "ingest + build";
		default:
			return "";
	}
});

function formatTime(value: string): string {
	return new Date(value).toLocaleString();
}

// Poll while a run is queued or in progress so the status and, on completion,
// the verification report appear without a manual reload.
let pollTimer: ReturnType<typeof setInterval> | null = null;

function stopPolling(): void {
	if (pollTimer !== null) {
		clearInterval(pollTimer);
		pollTimer = null;
	}
}

function syncPolling(): void {
	if (fabricRunning.value && pollTimer === null) {
		pollTimer = setInterval(refreshSiteFabric, 5000);
	} else if (!fabricRunning.value) {
		stopPolling();
	}
}

async function refreshSiteFabric(): Promise<void> {
	try {
		await orgStore.fetchSiteFabric();
	} catch (error) {
		console.error("Error fetching site fabric status:", error);
	} finally {
		syncPolling();
	}
}

const form = reactive({
	name: "",
	website: "",
	generalManagerName: "",
	officeAddressStreet: "",
	officeAddressCity: "",
	officeAddressPostalCode: "",
	officePhone: "",
	officeEmail: "",
	officeFax: "",
	unitPreset: "imperial",
	unitArea: "",
	unitMass: "",
	unitVolume: "",
	unitAreaBasis: "",
	unitVolumeBasis: "",
	// The routing card's own state. routingBase is the point the map placed (a
	// copy, because the store keeps the saved value), routingBaseLabel its
	// free-text name, and routingServiceMinutes one edit string per kind so a
	// blank row stays blank instead of becoming a zero.
	routingBase: null as RoutingBase | null,
	routingBaseLabel: "",
	routingCosting: "auto",
	routingServiceMinutes: {} as Record<string, string>,
});

const loadFormFromSettings = () => {
	const s = orgStore.settings;
	if (!s) return;
	form.name = s.name ?? "";
	form.website = s.website ?? "";
	form.generalManagerName = s.generalManagerName ?? "";
	form.officeAddressStreet = s.officeAddressStreet ?? "";
	form.officeAddressCity = s.officeAddressCity ?? "";
	form.officeAddressPostalCode = s.officeAddressPostalCode ?? "";
	form.officePhone = s.officePhone ?? "";
	form.officeEmail = s.officeEmail ?? "";
	form.officeFax = s.officeFax ?? "";
	form.unitPreset = s.unitPreference?.preset ?? "imperial";
	form.unitArea = s.unitPreference?.area ?? "";
	form.unitMass = s.unitPreference?.mass ?? "";
	form.unitVolume = s.unitPreference?.volume ?? "";
	form.unitAreaBasis = s.unitPreference?.areaBasis ?? "";
	form.unitVolumeBasis = s.unitPreference?.volumeBasis ?? "";
	form.routingBase = s.routingBase ? { ...s.routingBase } : null;
	form.routingBaseLabel = s.routingBase?.label ?? "";
	form.routingCosting = s.routingCosting ?? "auto";
	form.routingServiceMinutes = serviceMinuteFields(s.routingServiceMinutes);
};

// Watch for settings changes and update the form
watch(
	() => orgStore.settings,
	(newSettings) => {
		if (newSettings) {
			loadFormFromSettings();
		}
	},
	{ immediate: true },
);

// Each field's change queues its own save through the shared auto-save
// handle; the module builds the one-field patch so the wiring is testable.
const saveText = (field: OrganizationTextField, event: Event) =>
	saveTextField(runSave, orgStore, field, event);
const saveUnit = (field: UnitOverrideField, event: Event) =>
	saveUnitOverride(runSave, orgStore, field, event);
const savePreset = (event: Event) => saveUnitPreset(runSave, orgStore, event);

// The routing card. The base map is a vue-maplibre map like the rest of the
// product's, and one click names the point; the marker draws the base the page
// currently holds. The camera is an opening view and never moves itself.
const mapInstance = useMap();

/** Nothing to draw: the source stays mounted so the layer stack never changes
 * shape, and an empty collection paints nothing. */
const EMPTY_GEOJSON: GeoJSON.FeatureCollection = {
	type: "FeatureCollection",
	features: [],
};

/** The district boundary drawn under the base, so the operator can see where
 * in its own service area the yard sits. An empty collection when the district
 * has no area: the source stays mounted and draws nothing. */
const routingServiceArea = computed<GeoJSON.GeoJSON>(() => {
	return orgStore.settings?.serviceArea?.geometry ?? EMPTY_GEOJSON;
});

/** The base map's opening view: the district's own service area when it has
 * one, else the base itself, else the continental default. */
const routingMapCenter = computed<[number, number]>(() => {
	const area = orgStore.settings?.serviceArea;
	if (area) {
		const lng = (area.min.longitude + area.max.longitude) / 2;
		const lat = (area.min.latitude + area.max.latitude) / 2;
		if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat];
	}
	const base = form.routingBase;
	if (base !== null) return [base.lng, base.lat];
	return [-98.5, 39.5];
});

/** Close enough to see a yard inside its district, and wide enough to find the
 * district when it has no service area to frame. */
const routingMapZoom = computed<number>(() =>
	orgStore.settings?.serviceArea ? 9 : form.routingBase !== null ? 12 : 4,
);

/** The marker's position. The marker is behind `v-if`, so the fallback never
 * reaches the map; it exists to keep the binding non-nullable. */
const routingBaseCoordinates = computed<[number, number]>(() => {
	const base = form.routingBase;
	return base === null ? routingMapCenter.value : [base.lng, base.lat];
});

/** The minutes grid's edit strings, one per kind. A kind with no default is a
 * blank field, not a zero: §5.2 reads "no minutes" as no time estimate. */
function serviceMinuteFields(
	stored: Record<string, number> | undefined,
): Record<string, string> {
	const fields: Record<string, string> = {};
	for (const kind of KIND_OPTIONS) {
		const minutes = stored?.[kind.value];
		fields[kind.value] = minutes == null ? "" : String(minutes);
	}
	return fields;
}

/** The whole map as the server wants it, built from the visible rows. The PUT
 * replaces routingServiceMinutes outright, so a cleared row is a kind with no
 * default rather than an edit left behind. */
function serviceMinutePayload(): Record<string, number> {
	const payload: Record<string, number> = {};
	for (const kind of KIND_OPTIONS) {
		const raw = form.routingServiceMinutes[kind.value];
		const minutes = Number(raw);
		if (raw && raw.trim() !== "" && Number.isFinite(minutes) && minutes > 0) {
			payload[kind.value] = Math.round(minutes);
		}
	}
	return payload;
}

// Placing, naming and clearing the base are one patch each: the save carries
// only routingBase, so the district's other settings are left alone. A null
// clears the stored point (§5.1: an unconfigured base is legal).
async function storeRoutingBase(base: RoutingBase | null): Promise<void> {
	form.routingBase = base;
	form.routingBaseLabel = base?.label ?? "";
	await runSave(() => orgStore.saveChanges({ routingBase: base }));
}

function onRoutingMapClick(event: MapMouseEvent): void {
	const label = form.routingBaseLabel.trim();
	void storeRoutingBase({
		lng: Number(event.lngLat.lng.toFixed(6)),
		lat: Number(event.lngLat.lat.toFixed(6)),
		label: label === "" ? null : label,
	});
}

function saveRoutingBaseLabel(): void {
	const base = form.routingBase;
	if (base === null) return;
	const label = form.routingBaseLabel.trim();
	void storeRoutingBase({ ...base, label: label === "" ? null : label });
}

function clearRoutingBase(): void {
	void storeRoutingBase(null);
}

function saveRoutingCosting(): void {
	const costing = form.routingCosting;
	void runSave(() => orgStore.saveChanges({ routingCosting: costing }));
}

function saveRoutingServiceMinutes(): void {
	const minutes = serviceMinutePayload();
	// Read the accepted map back into the rows before the save is queued, so a
	// zero or a non-number reads as "no estimate" instead of as an edit that
	// vanished. Synchronous, so it never touches a row edited after this change.
	form.routingServiceMinutes = serviceMinuteFields(minutes);
	void runSave(() =>
		orgStore.saveChanges({ routingServiceMinutes: minutes }),
	);
}

// A crosshair says the map is an input. The click is bound to the map's own
// event because that is where the point comes from.
function onRoutingMapLoad(event: MglEvent<"load">): void {
	event.map.on("click", onRoutingMapClick);
	const canvas = mapInstance.map?.getCanvas();
	if (canvas) canvas.style.cursor = "crosshair";
}

onMounted(async () => {
	try {
		await orgStore.fetchSettings();
	} catch (error) {
		console.error("Error fetching organization data:", error);
	}
	await refreshSiteFabric();
});

onUnmounted(stopPolling);
</script>

<style scoped>
.settings-card {
	box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
	margin-bottom: 30px;
}

/* The base map is an input, not a workbench: it stays small enough to sit
   beside the fields it feeds. */
.routing-map {
	height: 240px;
	border-radius: 8px;
	overflow: hidden;
	border: 1px solid rgba(0, 0, 0, 0.125);
}

.routing-base-pin {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	border-radius: 50%;
	background: #198754;
	color: #fff;
	font-size: 15px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}
</style>
